import { observable, makeObservable, action, computed } from "mobx";
import { RootStore } from "src/stores/RootStore";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import i18next from "i18next";
import { GET_ACTIVE_MANAGER_USERS } from "src/api/cred";
import { INVOICE_PROPERTY, INVOICE_STATE, INVOICE_TYPE } from "./InvoiceEnums";

import { GetActiveManagerUsers } from "src/api/generated/GetActiveManagerUsers";
import { NetworkConfig } from "src/network/NetworkConfig";
import { ErpType, getRoleKey } from "src/network/User";
import { MessageType } from "src/components/notifications/Notifier";
import Axios from "src/network/Axios";
import { isAxiosError } from "axios";

export enum APPROVE_REJECT_INVOICE_STATE {
    INIT,
    REJECTING,
    APPROVING,
    FINISHED,
    SUCCESS,
    ERROR
}

export interface IUserToRejectTo {
    userid: string;
    name: string;
}

export enum APPROVE_REJECT_MODAL_TYPE {
    APPROVE,
    REJECT
}

export class ApproveRejectInvoiceStore {
    rootStore: RootStore;

    isModalDisplayed = false;
    displayedModalType: APPROVE_REJECT_MODAL_TYPE = APPROVE_REJECT_MODAL_TYPE.APPROVE;

    approveRejectInvoiceState: APPROVE_REJECT_INVOICE_STATE = APPROVE_REJECT_INVOICE_STATE.INIT;

    usersToRejectToData: IUserToRejectTo[] = [];
    currentUserToRejectToSearchQuery: string = "";
    selectedUserToRejectToQueryString: string = "";
    selectedUserToRejectTo?: IUserToRejectTo = undefined;
    rejectedComment: string = "";

    error = {
        noUserSelected: "",
        noRejectionCommentEntered: ""
    };

    approveLastVisaErrors = {
        missingAccounting: "",
        missingCreditor: "",
        missingDocument: "",
        missingInvoiceDate: "",
        missingAmount: ""
    };

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            approveRejectInvoiceState: observable,
            isModalDisplayed: observable,
            usersToRejectToData: observable,
            currentUserToRejectToSearchQuery: observable,
            selectedUserToRejectToQueryString: observable,
            selectedUserToRejectTo: observable,
            rejectedComment: observable,
            error: observable,
            displayedModalType: observable,

            init: action,
            reset: action,
            setIsModalDisplayed: action,
            setUsersToRejectToData: action,
            setCurrentUserToRejectToSearchQuery: action,
            setSelectedUserToRejectTo: action,
            setApproveRejectInvoiceState: action,
            rejectInvoice: action,
            approveInvoice: action,
            loadUsersToRejectTo: action,
            setRejectedComment: action,
            setSelectedUserToRejectToQueryString: action,
            setDisplayedModalType: action,
            validateRejectInvoice: action,

            filteredUsersToRejectToData: computed,
            canApproveInvoice: computed
        });
    }

    init = async () => {
        await this.loadUsersToRejectTo();
    };

    reset = () => {
        this.approveRejectInvoiceState = APPROVE_REJECT_INVOICE_STATE.INIT;
        this.currentUserToRejectToSearchQuery = "";
        this.selectedUserToRejectToQueryString = "";
        this.selectedUserToRejectTo = undefined;
        this.error.noUserSelected = "";
        this.error.noRejectionCommentEntered = "";
    };

    loadUsersToRejectTo = async () => {
        const { data }: { data: GetActiveManagerUsers } = await apolloClientInstance.query<GetActiveManagerUsers>({
            query: GET_ACTIVE_MANAGER_USERS
        });

        const usersToAssignInvoice: IUserToRejectTo[] = data.ums_v_customer_users.map((user) => {
            const assignPerson: IUserToRejectTo = {
                userid: user.userid,
                name: user.name ?? ""
            };

            return assignPerson;
        });

        this.setUsersToRejectToData(usersToAssignInvoice);
    };

    get filteredUsersToRejectToData(): IUserToRejectTo[] {
        const searchQuery = this.currentUserToRejectToSearchQuery ?? "";

        let filteredUsersToAssignData: IUserToRejectTo[] = [...this.usersToRejectToData];

        const filteredUsersToApplyDataBySearchQuery = filteredUsersToAssignData.filter((userToAssign) => {
            return userToAssign.name?.toLowerCase().includes(searchQuery.toLowerCase());
        });

        if (searchQuery === "") {
            return filteredUsersToAssignData;
        } else {
            return filteredUsersToApplyDataBySearchQuery;
        }
    }

    rejectInvoice = async (): Promise<boolean> => {
        this.setApproveRejectInvoiceState(APPROVE_REJECT_INVOICE_STATE.REJECTING);

        let wasInvoiceSuccessfullyRejected = false;

        const currentInvoice = this.rootStore.invoiceStore.currentInvoice;

        const rejectInvoiceVariables = {
            approved: false,
            invoiceid: currentInvoice?.id,
            workflowid: currentInvoice?.workflowInstance,
            state: currentInvoice?.state,
            rejectedcomment: this.rejectedComment,
            rejectedto: this.selectedUserToRejectTo ? this.selectedUserToRejectTo.userid : undefined
        };

        try {
            await Axios.getAuthInstance().post(NetworkConfig.approveOrRejectInvoiceUrl, rejectInvoiceVariables, {
                headers: {
                    "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                }
            });
            wasInvoiceSuccessfullyRejected = true;
            this.rootStore.invoiceStore.currentInvoice?.updateProperty(INVOICE_PROPERTY.STATE, INVOICE_STATE.REJECTED);
        } catch (error) {
            console.error("Error rejecting invoice: ", error);
        }

        if (wasInvoiceSuccessfullyRejected) {
            this.setApproveRejectInvoiceState(APPROVE_REJECT_INVOICE_STATE.FINISHED);
        } else {
            this.setApproveRejectInvoiceState(APPROVE_REJECT_INVOICE_STATE.INIT);
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.kredi_flow.action.reject_invoice.failure"),
                MessageType.ERROR
            );
        }
        return wasInvoiceSuccessfullyRejected;
    };

    approveInvoice = async (): Promise<boolean> => {
        this.setApproveRejectInvoiceState(APPROVE_REJECT_INVOICE_STATE.APPROVING);

        let wasInvoiceSuccessfullyApproved = false;
        // 403: the backend refused because the logged-in user is not the visa user of this invoice state (#26246)
        let isNotVisaUser = false;

        const { canApproveInvoice, errorString } = this.validateApproveInvoice();

        if (canApproveInvoice) {
            const currentInvoice = this.rootStore.invoiceStore.currentInvoice;

            const approveInvoiceVariables = {
                approved: true,
                invoiceid: currentInvoice?.id,
                workflowid: currentInvoice?.workflowInstance,
                state: currentInvoice?.state
            };

            try {
                await Axios.getAuthInstance().post(NetworkConfig.approveOrRejectInvoiceUrl, approveInvoiceVariables, {
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    }
                });
                wasInvoiceSuccessfullyApproved = true;
            } catch (error) {
                console.error("Error approving invoice: ", error);
                isNotVisaUser = isAxiosError(error) && error.response?.status === 403;
            }

            if (wasInvoiceSuccessfullyApproved) {
                this.setApproveRejectInvoiceState(APPROVE_REJECT_INVOICE_STATE.FINISHED);
            } else if (isNotVisaUser) {
                this.setApproveRejectInvoiceState(APPROVE_REJECT_INVOICE_STATE.INIT);
                this.setIsModalDisplayed(false);
                this.rootStore.uiStore.printStatusMessage(
                    i18next.t("screens.kredi_flow.action.approve_invoice.failure_not_visa_user"),
                    MessageType.ERROR
                );

                // the displayed state or visa users were stale - reload so the actions match the server again
                if (currentInvoice?.number !== undefined) {
                    await this.rootStore.invoiceStore.init(currentInvoice.number.toString());
                }
            } else {
                this.setApproveRejectInvoiceState(APPROVE_REJECT_INVOICE_STATE.INIT);
                this.rootStore.uiStore.printStatusMessage(
                    i18next.t("screens.kredi_flow.action.approve_invoice.failure"),
                    MessageType.ERROR
                );
            }
        } else {
            this.setApproveRejectInvoiceState(APPROVE_REJECT_INVOICE_STATE.INIT);
            this.rootStore.uiStore.printStatusMessage(errorString, MessageType.ERROR);
            this.setIsModalDisplayed(false);
        }

        return wasInvoiceSuccessfullyApproved;
    };

    get canApproveInvoice(): boolean {
        const hasSavedAccountings = this.rootStore.invoiceStore.currentInvoice?.hasSavedAccountings;
        const isLastVisaToApprove = this.rootStore.invoiceStore.currentInvoice?.isLastVisaToApprove;

        if (!hasSavedAccountings && isLastVisaToApprove) {
            return false;
        } else {
            return true;
        }
    }

    validateApproveInvoice = (): { canApproveInvoice: boolean; errorString: string } => {
        let canApproveInvoice = true;
        let approveInvoiceMissingFields = [];

        const isLastVisaToApprove = this.rootStore.invoiceStore.currentInvoice?.isLastVisaToApprove;

        if (isLastVisaToApprove) {
            const hasSavedAccountings = this.rootStore.invoiceStore.currentInvoice?.hasSavedAccountings;
            if (!hasSavedAccountings) {
                approveInvoiceMissingFields.push(i18next.t("screens.kredi_flow.accountings.section_title"));
                canApproveInvoice = false;
            }

            const hasCreditor =
                this.rootStore.invoiceStore.currentInvoice?.creditor &&
                this.rootStore.invoiceStore.currentInvoice?.creditor.id;
            if (!hasCreditor) {
                approveInvoiceMissingFields.push(i18next.t("screens.kredi_flow.list.creditor"));
                canApproveInvoice = false;
            }

            const hasDocument =
                this.rootStore.invoiceStore.currentInvoice?.invoiceFile &&
                this.rootStore.invoiceStore.currentInvoice?.invoiceFile.fileId;
            if (!hasDocument) {
                approveInvoiceMissingFields.push(i18next.t("screens.kredi_flow.document"));
                canApproveInvoice = false;
            }

            const hasInvoiceDate = this.rootStore.invoiceStore.currentInvoice?.date;
            if (!hasInvoiceDate) {
                approveInvoiceMissingFields.push(i18next.t("screens.kredi_flow.list.invoice_date"));
                canApproveInvoice = false;
            }

            const hasAmount = this.rootStore.invoiceStore.currentInvoice?.amount;
            if (!hasAmount) {
                approveInvoiceMissingFields.push(i18next.t("screens.kredi_flow.list.amount"));
                canApproveInvoice = false;
            }

            const hasIBAN =
                this.rootStore.invoiceStore.currentInvoice?.paymentAccount?.id ||
                this.rootStore.invoiceStore.currentInvoice?.paymentInfo?.iban;
            if (!hasIBAN && this.rootStore.invoiceStore.currentInvoice?.invoiceType !== INVOICE_TYPE.CREDIT) {
                approveInvoiceMissingFields.push(i18next.t("screens.kredi_flow.form.invoice.iban"));
                canApproveInvoice = false;
            }

            const erpType = this.rootStore.authStore.user?.erpType;

            if (erpType === ErpType.IT2) {
                const hasPayoutAccount = this.rootStore.invoiceStore.currentInvoice?.payoutaccountid;

                if (
                    !hasPayoutAccount &&
                    this.rootStore.invoiceStore.currentInvoice?.invoiceType !== INVOICE_TYPE.CREDIT
                ) {
                    approveInvoiceMissingFields.push(i18next.t("screens.kredi_flow.accountings.payout_account"));
                    canApproveInvoice = false;
                }
            }
        }

        const missingFieldsCount = approveInvoiceMissingFields.length;

        let missingFieldsString = "";
        if (missingFieldsCount === 1) {
            missingFieldsString = approveInvoiceMissingFields[0];
        } else if (missingFieldsCount === 2) {
            missingFieldsString = approveInvoiceMissingFields.join(` ${i18next.t("labels.and")} `);
        } else if (missingFieldsCount > 2) {
            missingFieldsString =
                approveInvoiceMissingFields.slice(0, -1).join(", ") +
                ` ${i18next.t("labels.and")} ` +
                approveInvoiceMissingFields[missingFieldsCount - 1];
        }

        const approveInvoiceErrorString =
            missingFieldsCount === 0
                ? ""
                : i18next.t("screens.kredi_flow.action.approve_invoice.approve_last_visa_failure", {
                      missing_fields_string: missingFieldsString
                  });

        return { canApproveInvoice: canApproveInvoice, errorString: approveInvoiceErrorString };
    };

    validateRejectInvoice = (): boolean => {
        let isValid = true;

        if (!this.selectedUserToRejectTo) {
            this.error.noUserSelected = i18next
                .t("screens.kredi_flow.action.reject_invoice.no_user_selected")
                .toString();

            isValid = false;
        } else {
            this.error.noUserSelected = "";
        }

        if (!this.rejectedComment) {
            this.error.noRejectionCommentEntered = i18next
                .t("screens.kredi_flow.action.reject_invoice.no_rejection_comment_entered")
                .toString();

            isValid = false;
        } else {
            this.error.noRejectionCommentEntered = "";
        }

        return isValid;
    };

    /* SETTERS */
    setIsModalDisplayed = (isModalDisplayed: boolean) => {
        this.isModalDisplayed = isModalDisplayed;
    };

    setUsersToRejectToData = (usersToRejectoToData: IUserToRejectTo[]) => {
        this.usersToRejectToData = usersToRejectoToData;
    };

    setCurrentUserToRejectToSearchQuery = (currentUserToRejectToSearchQuery: string) => {
        this.currentUserToRejectToSearchQuery = currentUserToRejectToSearchQuery;
    };

    setSelectedUserToRejectTo = (selectedUserToRejectTo: IUserToRejectTo) => {
        this.selectedUserToRejectTo = selectedUserToRejectTo;
    };

    setApproveRejectInvoiceState = (assignInvoiceState: APPROVE_REJECT_INVOICE_STATE) => {
        this.approveRejectInvoiceState = assignInvoiceState;
    };

    setRejectedComment = (rejectedComment: string) => {
        this.rejectedComment = rejectedComment;
    };

    setSelectedUserToRejectToQueryString = (selectedUserToRejectToQueryString: string) => {
        this.selectedUserToRejectToQueryString = selectedUserToRejectToQueryString;
    };

    setDisplayedModalType = (displayedModalType: APPROVE_REJECT_MODAL_TYPE) => {
        this.displayedModalType = displayedModalType;
    };
}
